import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  ImageIcon,
  Info,
  Loader2,
  Plus,
  Search,
} from 'lucide-react';
import notify from '../../../utils/notify';
import { formatPrice, getRowErrors, rowIssues, summarizeRows } from './productImport';
import ImportIssues, { ISSUE_TITLES } from './ImportIssues';

const IMAGE_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Rows are drawn 100 at a time: each one carries three dropdowns, and a whole
// catalog of them at once is a few hundred thousand elements.
const ROWS_PER_PAGE = 100;

// What the bulk bar can set on every row shown.
const BULK_FIELDS = [
  { key: 'salesCode', label: 'Sales code', noun: 'sales code' },
  { key: 'category', label: 'Category', noun: 'category' },
  { key: 'vendor', label: 'Vendor', noun: 'vendor' },
  { key: 'price', label: 'Trade price', noun: 'trade price', money: true },
  { key: 'sellingPrice', label: 'Selling price', noun: 'selling price', money: true },
];

const PLACEHOLDER_NOUNS = {
  sku: 'SKU',
  finishes: 'finishes',
  materials: 'materials',
  color: 'color',
  link: 'link',
  length: 'length',
  width: 'width',
  height: 'height',
  depth: 'depth',
};

function MoneyInput({ id, value, onChange, error, label }) {
  return (
    <div className="pi-money-cell">
      <div className={`pi-money${error ? ' has-error' : ''}`}>
        <span aria-hidden="true">$</span>
        <input
          id={id}
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          aria-invalid={Boolean(error)}
        />
      </div>
      {error && <span className="pi-field-error">{error}</span>}
    </div>
  );
}

// A photo from the file's link stops loading once the link expires; the
// placeholder shows then.
function Thumb({ src }) {
  const [failed, setFailed] = useState('');
  if (!src || failed === src) return <ImageIcon size={18} aria-hidden="true" />;
  return <img src={src} alt="" onError={() => setFailed(src)} />;
}

function DetailField({ id, label, children }) {
  return (
    <div className="pi-detail-field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

// One table row. Memoized so typing in one row doesn't redraw a few hundred
// others: every prop is either the row itself or stable between edits.
const ReviewRow = memo(function ReviewRow({
  row,
  isActive,
  uploading,
  flagVendor,
  categoryChoices,
  salesCodeOptions,
  vendorOptions,
  onUpdateRow,
  onSelectRows,
  onActivate,
  onPickImage,
}) {
  const errors = row.selected ? getRowErrors(row) : {};
  const warn = (missing) => (row.selected && missing ? 'is-warning' : '');
  const setField = (key, value) => onUpdateRow(row.id, { fields: { [key]: value } });
  const classes = [isActive ? 'is-active' : '', row.selected ? '' : 'is-unselected']
    .filter(Boolean)
    .join(' ');

  return (
    <tr className={classes} onClick={() => onActivate(row.id)}>
      <td className="pi-col-check">
        <input
          id={`pi-select-${row.id}`}
          type="checkbox"
          checked={row.selected}
          onChange={(e) => onSelectRows([row.id], e.target.checked)}
          aria-label={`Import ${row.fields.name || 'this row'}`}
        />
      </td>
      <td className="pi-col-product">
        <span className={`pi-product-name${errors.name ? ' has-error' : ''}`}>
          {row.fields.name || 'Name required'}
        </span>
        {row.alreadyInCatalog && (
          <span className="pi-chip pi-chip--muted">Already in Products &amp; Services</span>
        )}
        {row.archived && !row.alreadyInCatalog && (
          <span className="pi-chip pi-chip--muted">Archived in the old system</span>
        )}
        {row.nonProduct && !row.archived && !row.alreadyInCatalog && (
          <span className="pi-chip pi-chip--muted">Labor, fee or expense</span>
        )}
      </td>
      <td className="pi-col-image">
        <div className="pi-image-cell">
          <div className="pi-thumb">
            {uploading ? (
              <Loader2 size={18} className="pi-spin" aria-label="Uploading" />
            ) : (
              <Thumb src={row.imageUrl || row.imageLink} />
            )}
          </div>
          <div className="pi-image-meta">
            <button
              type="button"
              className="pi-link"
              onClick={() => onPickImage(row.id)}
              disabled={uploading}
            >
              {row.imageUrl || row.imageLink ? 'Replace image' : 'Upload image'}
            </button>
            {row.imageFile && (
              <span className="pi-image-name" title={row.imageFile}>
                {row.imageFile}
              </span>
            )}
          </div>
        </div>
      </td>
      <td>
        <select
          id={`pi-category-${row.id}`}
          className={warn(!row.fields.category)}
          value={row.fields.category}
          onChange={(e) => setField('category', e.target.value)}
          aria-label="Category"
        >
          <option value="">No category</option>
          {categoryChoices.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          id={`pi-sales-code-${row.id}`}
          className={warn(!row.fields.salesCode)}
          value={row.fields.salesCode}
          onChange={(e) => setField('salesCode', e.target.value)}
          aria-label="Sales code"
        >
          <option value="">Select sales code</option>
          {salesCodeOptions.map((option) => (
            <option key={`${option.name}-${option.salesCode}`} value={option.salesCode}>
              {option.salesCode} - {option.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          id={`pi-vendor-${row.id}`}
          className={warn(flagVendor && !row.vendorChoice)}
          value={row.vendorChoice}
          onChange={(e) => onUpdateRow(row.id, { vendorChoice: e.target.value })}
          aria-label="Vendor"
        >
          <option value="">No vendor</option>
          {vendorOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <MoneyInput
          id={`pi-price-${row.id}`}
          value={row.fields.price}
          onChange={(value) => setField('price', value)}
          error={errors.price}
          label="Trade price per unit"
        />
      </td>
      <td>
        <MoneyInput
          id={`pi-sell-${row.id}`}
          value={row.fields.sellingPrice}
          onChange={(value) => setField('sellingPrice', value)}
          error={errors.sellingPrice}
          label="Selling price per unit"
        />
      </td>
    </tr>
  );
});

export default function ImportReviewStep({
  itemType,
  fields,
  fileName,
  rows,
  rowStats,
  onUpdateRow,
  onSelectRows,
  onApplyToRows,
  onFillTradePrices,
  categoryChoices,
  vendors,
  salesCodeOptions,
  sellPrice,
  uploadingRowIds,
  onUploadRowImage,
  onUploadMatchingImages,
  onAddVendor,
  onChangeMapping,
  onBack,
  onSaveDraft,
  onImport,
  importing,
}) {
  const isService = itemType === 'service';
  const noun = isService ? 'service' : 'product';
  const nouns = isService ? 'services' : 'products';
  const label = (key) => fields.find((f) => f.key === key)?.label || key;

  const [query, setQuery] = useState('');
  const [rowLimit, setRowLimit] = useState(ROWS_PER_PAGE);
  const [activeId, setActiveId] = useState(rows[0]?.id || '');
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  // An issue's rows as they were when "Fix these" was clicked, so fixing one
  // doesn't drop it out of the table mid-edit.
  const [issueFilter, setIssueFilter] = useState(null);
  const [bulkField, setBulkField] = useState(salesCodeOptions.length ? 'salesCode' : 'category');
  const [bulkValue, setBulkValue] = useState('');
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);
  const rowImageInputRef = useRef(null);
  const matchingInputRef = useRef(null);
  const imageTargetRef = useRef('');

  const summary = useMemo(() => summarizeRows(rows, itemType), [rows, itemType]);
  const bulkFields = useMemo(
    () => BULK_FIELDS.filter((field) => field.key !== 'salesCode' || salesCodeOptions.length > 0),
    [salesCodeOptions.length]
  );
  const fixableKeys = useMemo(() => new Set(bulkFields.map((field) => field.key)), [bulkFields]);
  const bulk = bulkFields.find((field) => field.key === bulkField) || bulkFields[0];

  // Keyed on the names so the options keep their identity between edits.
  const newVendorKey = useMemo(
    () =>
      [...new Set(rows.map((r) => r.vendorChoice).filter((c) => c.startsWith('new:')))]
        .sort()
        .join('\n'),
    [rows]
  );
  const vendorOptions = useMemo(() => {
    const existing = [...vendors]
      .filter((v) => v.vendorId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((v) => ({ value: v.vendorId, label: v.name || v.companyName || 'Vendor' }));
    const fromFile = newVendorKey
      ? newVendorKey
          .split('\n')
          .map((choice) => ({ value: choice, label: `${choice.slice(4)} (new)` }))
      : [];
    return [...existing, ...fromFile];
  }, [vendors, newVendorKey]);

  const visibleRows = useMemo(() => {
    const inIssue = issueFilter ? rows.filter((row) => issueFilter.ids.has(row.id)) : rows;
    const q = query.trim().toLowerCase();
    if (!q) return inIssue;
    const vendorLabels = new Map(vendorOptions.map((option) => [option.value, option.label]));
    return inIssue.filter((row) =>
      [row.fields.name, row.fields.category, row.fields.sku, vendorLabels.get(row.vendorChoice)]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query, vendorOptions, issueFilter]);

  const renderedRows = visibleRows.slice(0, rowLimit);
  const hiddenCount = visibleRows.length - renderedRows.length;
  const active = rows.find((row) => row.id === activeId) || rows[0];
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => row.selected);
  const pendingImageFiles = rows.some((row) => row.imageFile && !row.imageUrl && !row.imageLink);
  // Copied in at import: see ProductImportWizard.
  const linkedPhotos = rows.filter(
    (row) => row.selected && row.imageLink && (!row.imageUrl || row.imageUrl === row.imageLink)
  ).length;

  const setField = (rowId, key, value) => onUpdateRow(rowId, { fields: { [key]: value } });

  const pickRowImage = useCallback((rowId) => {
    imageTargetRef.current = rowId;
    rowImageInputRef.current?.click();
  }, []);

  const handleRowImage = (e) => {
    const file = e.target.files?.[0];
    if (file && imageTargetRef.current) onUploadRowImage(imageTargetRef.current, file);
    e.target.value = '';
  };

  const handleMatchingImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onUploadMatchingImages(files);
    e.target.value = '';
  };

  const toggleIssue = (key) => {
    setRowLimit(ROWS_PER_PAGE);
    if (issueFilter?.key === key) {
      setIssueFilter(null);
      return;
    }
    const ids = new Set(
      rows
        .filter((row) => row.selected && rowIssues(row, itemType).includes(key))
        .map((row) => row.id)
    );
    setIssueFilter({ key, ids });
    setQuery('');
    if (fixableKeys.has(key)) {
      setBulkField(key);
      setBulkValue('');
    }
    // The details panel opens on the first one, where a name can be typed in.
    const first = rows.find((row) => ids.has(row.id));
    if (first) setActiveId(first.id);
  };

  // Unticks every selected row with a required problem, for rows that aren't
  // worth fixing (a library entry that never had a price).
  const leaveOut = (key) => {
    const ids = rows
      .filter((row) => row.selected && rowIssues(row, itemType).includes(key))
      .map((row) => row.id);
    if (!ids.length) return;
    onSelectRows(ids, false);
    if (issueFilter?.key === key) setIssueFilter(null);
    notify.success(`Left out ${plural(ids.length, noun, nouns)}. Tick them again to import them.`);
  };

  const q = query.trim();
  let bulkScope = `all ${plural(visibleRows.length, noun, nouns)}`;
  if (q) bulkScope = `the ${plural(visibleRows.length, noun, nouns)} matching “${q}”`;
  else if (issueFilter) bulkScope = `the ${plural(visibleRows.length, noun, nouns)} shown`;
  const bulkLabel = `Set the ${bulk.noun} for ${bulkScope}`;
  const bulkMoney = bulk.money ? formatPrice(bulkValue) : '';
  const bulkReady =
    visibleRows.length > 0 && (bulk.money ? bulkMoney !== '' : bulkValue.trim() !== '');

  const applyBulk = () => {
    if (!bulkReady) return;
    let value = bulkValue;
    let shown = bulkValue;
    if (bulk.money) {
      value = bulkMoney;
      shown = `$${bulkMoney}`;
    } else if (bulk.key === 'category') {
      // Reuse the catalog's spelling, so "lighting" doesn't add a second Lighting.
      const typed = bulkValue.replace(/\s+/g, ' ').trim();
      value = categoryChoices.find((name) => name.toLowerCase() === typed.toLowerCase()) || typed;
      shown = value;
    } else if (bulk.key === 'vendor') {
      shown = vendorOptions.find((o) => o.value === value)?.label.replace(/ \(new\)$/, '') || value;
    }
    onApplyToRows(
      visibleRows.map((row) => row.id),
      bulk.key,
      value
    );
    notify.success(
      `Set the ${bulk.noun} to ${shown} on ${plural(visibleRows.length, noun, nouns)}.`
    );
    setBulkValue('');
  };

  let bulkInput;
  if (bulk.money) {
    bulkInput = (
      <div className="pi-money pi-bulk-money">
        <span aria-hidden="true">$</span>
        <input
          id="pi-bulk-value"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="0.00"
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
          aria-label={bulkLabel}
        />
      </div>
    );
  } else if (bulk.key === 'category') {
    bulkInput = (
      <>
        <input
          id="pi-bulk-value"
          list="pi-bulk-categories"
          placeholder="Choose or type a category"
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
          aria-label={bulkLabel}
        />
        <datalist id="pi-bulk-categories">
          {categoryChoices.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </>
    );
  } else {
    const options =
      bulk.key === 'salesCode'
        ? salesCodeOptions.map((option) => ({
            key: `${option.name}-${option.salesCode}`,
            value: option.salesCode,
            label: `${option.salesCode} - ${option.name}`,
          }))
        : vendorOptions.map((option) => ({ ...option, key: option.value }));
    bulkInput = (
      <select
        id="pi-bulk-value"
        value={bulkValue}
        onChange={(e) => setBulkValue(e.target.value)}
        aria-label={bulkLabel}
      >
        <option value="">Choose a {bulk.noun}</option>
        {options.map((option) => (
          <option key={option.key} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  const submitVendor = async (e) => {
    e.preventDefault();
    const name = newVendorName.trim();
    if (!name || savingVendor) return;
    setSavingVendor(true);
    const created = await onAddVendor(name, active?.vendorChoice ? '' : active?.id);
    setSavingVendor(false);
    if (created) {
      setNewVendorName('');
      setAddVendorOpen(false);
    }
  };

  const blocked = importing || summary.selected === 0 || summary.blocking > 0;
  const detailMainFields = isService
    ? ['name', 'description']
    : ['name', 'sku', 'finishes', 'materials', 'description'];
  const detailMoreFields = isService
    ? ['link', 'notes']
    : [
        'color',
        'msrp',
        'shipping',
        'tariff',
        'link',
        'notes',
        'length',
        'width',
        'height',
        'depth',
      ];
  const moneyKeys = new Set(['msrp', 'shipping', 'tariff']);

  const renderDetailInput = (key) => {
    const id = `pi-detail-${key}`;
    const value = active.fields[key] ?? '';
    const onChange = (e) => setField(active.id, key, e.target.value);
    if (key === 'description' || key === 'notes') {
      return (
        <DetailField key={key} id={id} label={label(key)}>
          <textarea id={id} rows={2} value={value} onChange={onChange} />
        </DetailField>
      );
    }
    if (moneyKeys.has(key)) {
      return (
        <DetailField key={key} id={id} label={label(key)}>
          <input
            id={id}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="0.00"
            value={value}
            onChange={onChange}
          />
        </DetailField>
      );
    }
    const errors = key === 'name' ? getRowErrors(active) : {};
    return (
      <DetailField key={key} id={id} label={label(key)}>
        <input
          id={id}
          value={value}
          onChange={onChange}
          placeholder={key === 'name' ? '' : `Enter ${PLACEHOLDER_NOUNS[key] || key} (optional)`}
          aria-invalid={Boolean(errors.name)}
          className={errors.name ? 'has-error' : ''}
        />
      </DetailField>
    );
  };

  let sellPriceNote = 'Selling prices are not being imported.';
  if (sellPrice.mode === 'withMarkup' || sellPrice.mode === 'tradeMarkup') {
    sellPriceNote = `Selling prices are ${sellPrice.header} plus ${sellPrice.markupHeader}.`;
  } else if (sellPrice.mode === 'file') {
    sellPriceNote = `Selling prices use ${sellPrice.header} as exported.`;
  }

  return (
    <>
      <div className="pi-card pi-file-chip">
        <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
        <span>{fileName}</span>
      </div>

      <ImportIssues
        summary={summary}
        noun={noun}
        nouns={nouns}
        activeKey={issueFilter?.key || ''}
        fixableKeys={fixableKeys}
        onToggle={toggleIssue}
        onLeaveOut={leaveOut}
        onFillTradePrices={onFillTradePrices}
        pendingImageFiles={pendingImageFiles}
        onUploadMatchingImages={() => matchingInputRef.current?.click()}
        hasSalesCodes={salesCodeOptions.length > 0}
      />

      <section className="pi-card pi-review-card">
        <div className="pi-review-head">
          <strong>{plural(summary.selected, `${noun} selected`, `${nouns} selected`)}</strong>
          <label className="pi-search">
            <Search size={16} aria-hidden="true" />
            <input
              id="pi-review-search"
              type="search"
              placeholder={`Search ${nouns}...`}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setRowLimit(ROWS_PER_PAGE);
              }}
              aria-label={`Search ${nouns}`}
            />
          </label>
        </div>

        <div className="pi-bulk">
          <span className="pi-bulk-label">Set</span>
          <select
            id="pi-bulk-field"
            className="pi-bulk-field"
            value={bulk.key}
            onChange={(e) => {
              setBulkField(e.target.value);
              setBulkValue('');
            }}
            aria-label="Field to set"
          >
            {bulkFields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
          <span className="pi-bulk-label">for {bulkScope}</span>
          {bulkInput}
          <button
            type="button"
            className="pi-btn pi-btn--outline-accent"
            onClick={applyBulk}
            disabled={!bulkReady}
          >
            Apply
          </button>
          <span className="pi-bulk-hint">
            Search, or use Fix these above, to set it on a group.
          </span>
        </div>

        {issueFilter && (
          <div className="pi-filter-note" role="status">
            <span>
              Showing {plural(issueFilter.ids.size, noun, nouns)} from “
              {ISSUE_TITLES[issueFilter.key]}”.
            </span>
            <button type="button" className="pi-link" onClick={() => setIssueFilter(null)}>
              Show all {nouns}
            </button>
          </div>
        )}

        <div className="pi-table-wrap pi-review-table-wrap">
          <table className="pi-table pi-review-table">
            <thead>
              <tr>
                <th className="pi-col-check">
                  <input
                    id="pi-select-all"
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) =>
                      onSelectRows(
                        visibleRows.map((row) => row.id),
                        e.target.checked
                      )
                    }
                    aria-label={`Select all ${nouns}`}
                  />
                </th>
                <th>{isService ? 'Service' : 'Product'}</th>
                <th>Image</th>
                <th>Category</th>
                <th>Sales Code</th>
                <th>Vendor</th>
                <th>Trade price / unit</th>
                <th>Selling price / unit</th>
              </tr>
            </thead>
            <tbody>
              {renderedRows.map((row) => (
                <ReviewRow
                  key={row.id}
                  row={row}
                  isActive={row.id === active?.id}
                  uploading={uploadingRowIds.has(row.id)}
                  flagVendor={!isService}
                  categoryChoices={categoryChoices}
                  salesCodeOptions={salesCodeOptions}
                  vendorOptions={vendorOptions}
                  onUpdateRow={onUpdateRow}
                  onSelectRows={onSelectRows}
                  onActivate={setActiveId}
                  onPickImage={pickRowImage}
                />
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="pi-empty">
                    No {nouns} match &ldquo;{query}&rdquo;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {hiddenCount > 0 && (
          <div className="pi-more-rows">
            <span>
              Showing {renderedRows.length} of {visibleRows.length}. Search to find a {noun} faster.
            </span>
            <button
              type="button"
              className="pi-btn pi-btn--outline"
              onClick={() => setRowLimit((limit) => limit + ROWS_PER_PAGE)}
            >
              Show {Math.min(ROWS_PER_PAGE, hiddenCount)} more
            </button>
          </div>
        )}
      </section>

      <div className="pi-review-bottom">
        {active && (
          <section className="pi-card pi-details">
            <button
              type="button"
              className="pi-details-toggle"
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
            >
              <span>
                <strong>{isService ? 'Service' : 'Product'} details</strong>
                <span className="pi-muted">
                  {' '}
                  · Excel row{active.sourceRows.length > 1 ? 's' : ''}{' '}
                  {active.sourceRows.join(', ')}
                </span>
              </span>
              {detailsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {detailsOpen && (
              <div className="pi-details-body">
                {active.sourceRows.length > 1 && (
                  <p className="pi-details-note">
                    These rows repeat the same {noun}, so they'll be saved as one.
                  </p>
                )}
                {detailMainFields.map(renderDetailInput)}
                {active.customSpecs?.length > 0 && (
                  <div className="pi-detail-field">
                    <span className="pi-detail-label">Custom specs</span>
                    <ul className="pi-spec-list">
                      {active.customSpecs.map((spec) => (
                        <li key={spec.label}>
                          <strong>{spec.label}:</strong> {spec.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  type="button"
                  className="pi-link pi-more-toggle"
                  onClick={() => setMoreOpen((open) => !open)}
                  aria-expanded={moreOpen}
                >
                  {isService
                    ? 'Link & internal notes'
                    : 'Shipping, tariff cost & additional details'}
                  {moreOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </button>
                {moreOpen && detailMoreFields.map(renderDetailInput)}
              </div>
            )}
          </section>
        )}

        <aside className="pi-card pi-summary">
          <h3>Import summary</h3>
          <ul className="pi-summary-list">
            <li className="is-ok">
              <CheckCircle2 size={18} aria-hidden="true" />
              {plural(summary.selected, `${noun} selected`, `${nouns} selected`)}
            </li>
            {summary.blocking > 0 && (
              <li className="is-error">
                <AlertCircle size={18} aria-hidden="true" />
                {plural(summary.blocking, 'required item', 'required items')} to fix under Needs
                attention
              </li>
            )}
          </ul>

          <div className="pi-summary-notes">
            {rowStats.combinedRows > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  {plural(rowStats.combinedRows, 'repeated row was', 'repeated rows were')} combined
                  with the {noun} {rowStats.combinedRows === 1 ? 'it repeats' : 'they repeat'}.
                </span>
              </p>
            )}
            {rowStats.alreadyInCatalog > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  {plural(rowStats.alreadyInCatalog, `${noun} is`, `${nouns} are`)} already in
                  Products &amp; Services and left unselected.
                </span>
              </p>
            )}
            {rowStats.archived > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  {plural(rowStats.archived, `${noun} was`, `${nouns} were`)} archived in the old
                  system and left unselected.
                </span>
              </p>
            )}
            {rowStats.nonProduct > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  {plural(
                    rowStats.nonProduct,
                    'labor, fee or expense line was',
                    'labor, fee and expense lines were'
                  )}{' '}
                  left unselected. Tick any that belong in the catalog.
                </span>
              </p>
            )}
            {linkedPhotos > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  Photos for {plural(linkedPhotos, noun, nouns)} will be copied in from the
                  file&apos;s links when you import. Ivy&apos;s links stop working about a day after
                  the export.
                </span>
              </p>
            )}
            {rowStats.expiredPhotos > 0 && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  The photo links for {plural(rowStats.expiredPhotos, noun, nouns)} have expired.
                  Export the file again and import it within a day to bring those photos in, or
                  upload the image files.
                </span>
              </p>
            )}
            {sellPrice.mapped && (
              <p>
                <Info size={16} aria-hidden="true" />
                <span>
                  {sellPriceNote}{' '}
                  <button type="button" className="pi-link" onClick={onChangeMapping}>
                    Change column mapping
                  </button>
                </span>
              </p>
            )}
          </div>

          <div className="pi-summary-vendor">
            <p>Choose an existing vendor or add a new vendor.</p>
            <button
              type="button"
              className="pi-btn pi-btn--outline-accent"
              onClick={() => setAddVendorOpen(true)}
            >
              <Plus size={16} aria-hidden="true" /> Add vendor
            </button>
          </div>
        </aside>
      </div>

      <div className="pi-footer">
        <button
          type="button"
          className="pi-btn pi-btn--outline"
          onClick={onBack}
          disabled={importing}
        >
          <ArrowLeft size={16} aria-hidden="true" /> Back to Map Columns
        </button>
        <div className="pi-footer-actions">
          <button
            type="button"
            className="pi-btn pi-btn--outline-accent"
            onClick={onSaveDraft}
            disabled={Boolean(importing)}
          >
            Save draft
          </button>
          <div className="pi-import-action">
            <button
              type="button"
              className="pi-btn pi-btn--primary pi-btn--lg"
              onClick={onImport}
              disabled={blocked}
            >
              {importing ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" />
                  Importing {importing.done} of {importing.total}…
                </>
              ) : (
                <>
                  Import {plural(summary.selected, noun, nouns)}{' '}
                  <ArrowRight size={16} aria-hidden="true" />
                </>
              )}
            </button>
            {!importing && summary.blocking > 0 && (
              <span className="pi-footer-hint">
                Fix the required items under Needs attention to import.
              </span>
            )}
            {!importing && summary.blocking === 0 && summary.selected === 0 && (
              <span className="pi-footer-hint">Select at least one {noun} to import.</span>
            )}
          </div>
        </div>
      </div>

      <input
        ref={rowImageInputRef}
        id="pi-row-image-input"
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={handleRowImage}
        hidden
      />
      <input
        ref={matchingInputRef}
        id="pi-matching-images-input"
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        onChange={handleMatchingImages}
        hidden
      />

      {addVendorOpen && (
        <div className="pi-modal-overlay" onClick={() => setAddVendorOpen(false)}>
          <div
            className="pi-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pi-add-vendor-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="pi-add-vendor-title">Add vendor</h3>
            <form onSubmit={submitVendor}>
              <label htmlFor="pi-new-vendor">Vendor name</label>
              <input
                id="pi-new-vendor"
                autoFocus
                value={newVendorName}
                onChange={(e) => setNewVendorName(e.target.value)}
                placeholder="e.g. Visual Comfort"
              />
              <div className="pi-modal-actions">
                <button
                  type="button"
                  className="pi-btn pi-btn--outline"
                  onClick={() => setAddVendorOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pi-btn pi-btn--primary"
                  disabled={!newVendorName.trim() || savingVendor}
                >
                  {savingVendor ? 'Adding…' : 'Add vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
